import React from 'react';
import Div100vh from 'react-div-100vh';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { EffectFade, Mousewheel, Navigation } from 'swiper';
import { generatePath, useHistory } from 'react-router';
import { defer } from 'lodash';
import cn from 'classnames';

import { useTranslation, useAppSelector, useResponsive, useAppDispatch } from 'client/hooks';

import { ControlPanel, Header } from 'client/components/common/mobile';
import { DoubleImageSlide, HelmetComponent, Loader, PageError } from 'client/components/common';

import { selectLang } from 'client/redux/settings/selectors';
import { main as mainApi } from 'client/redux/api/main';
import { selectAuth, selectUserRole } from 'client/redux/user/selectors';
import { INVESTMENT_OPPORTUNITIES, MAIN_PAGE_ROUTE } from 'client/constants/routes';
import { setPopup } from 'client/redux/ui';

import bg from 'client/assets/invest-opp/investment-opp-bg.jpg';

import css from './InvestmentOpportunities.module.scss';

SwiperCore.use([EffectFade, Mousewheel, Navigation]);
const SLIDES_PER_GROUP = 4;

const InvestmentOpportunities = () => {
  const { translate } = useTranslation();
  const auth = useAppSelector(selectAuth);
  const history = useHistory();
  const lang = useAppSelector(selectLang);
  const dispatch = useAppDispatch();
  const sliderRef = React.useRef<SwiperCore>();
  const [isMobile] = useResponsive(['MOBILE']);
  const userRole = useAppSelector(selectUserRole);
  const [isHideNext, setIsHideNext] = React.useState<boolean>(false);
  const [isHidePrev, setIsHidePrev] = React.useState<boolean>(true);

  const { data: sliderDataResponse, isLoading } = mainApi.endpoints.getMainPageSliders.useQuery({
    type: ['comingSoonSlider', 'investNowSlider'],
  });

  const slidersData = sliderDataResponse?.success ? sliderDataResponse.data : undefined;
  const items = slidersData?.investNowSlider ?? [];

  const onSlideChange = (s: SwiperCore) => {
    if (items) {
      if (s.activeIndex === 0) {
        setIsHidePrev(true);
      } else {
        setIsHidePrev(false);
      }
      if (s.activeIndex + SLIDES_PER_GROUP >= items.length) {
        setIsHideNext(true);
      } else {
        setIsHideNext(false);
      }
    }
  };

  const renderPageContent = () => {
    return (
      <div className={css.investmentOpportunities}>
        <div className={css.hero}>
          <div className={css.mainBg} style={{ backgroundImage: `url('${bg}')` }}></div>
          <div className={css.bottomGradient} />
          <div className={css.leftGradient} />
          <div className={css.content}>
            <div className={css.title}>{translate('investmentOpp.title')}</div>
            <div className={css.subtitle}>
              <p>{translate('investmentOpp.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className={css.companies}>
          {isLoading ? (
            <Loader position="relative" />
          ) : (
            <div className={css.list}>
              {items.map((item) => (
                <DoubleImageSlide
                  className={css.item}
                  key={item._id}
                  item={item}
                  isUpvoteDisabled
                  isMandateLink={Boolean(item.mandateInfo)}
                  buttonLabel={translate('mainPageDesktop.watchButton')}
                />
              ))}
            </div>
          )}
        </div>

        <div className={css.comingSoonSlider}>
          <div className={css.title}>{translate('investmentOpp.comingSoonSliderTitle')}</div>
          <div className={css.itemsWrapper}>
            <Swiper
              spaceBetween={0}
              slidesPerView="auto"
              mousewheel={false}
              onSwiper={(swiper) => (sliderRef.current = swiper)}
              onSlideChange={onSlideChange}
            >
              {slidersData?.comingSoonSlider?.map((i, index) => (
                <SwiperSlide style={{ width: 'fit-content' }} key={index}>
                  <DoubleImageSlide
                    item={i}
                    className={css.slide}
                    buttonLabel={translate('mainPageDesktop.watchButton')}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {items && items.length > SLIDES_PER_GROUP && (
              <>
                <div
                  className={cn(css.navBtn, css.next, isHideNext && css.hide)}
                  onClick={() => sliderRef.current?.slideNext()}
                >
                  <div className={css.arrow}></div>
                </div>

                <div
                  className={cn(css.navBtn, css.prev, isHidePrev && css.hide)}
                  onClick={() => sliderRef.current?.slidePrev()}
                >
                  <div className={css.arrow}></div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  React.useEffect(() => {
    if (auth.isLoggedIn && userRole === 'VIEWER') {
      defer(() => {
        dispatch(
          setPopup({
            type: 'join-as-investor',
            data: { link: generatePath(INVESTMENT_OPPORTUNITIES, { lang }), isExternalLink: false },
          })
        );
        history.push(generatePath(MAIN_PAGE_ROUTE, { lang }));
      }, 0);
    }
  }, [auth.isLoggedIn]);

  if ((!auth.isLoggedIn && auth.isCompleted) || (auth.isLoggedIn && userRole === 'VIEWER')) {
    return <PageError message="Unable to get page data" />;
  }

  return (
    <>
      <HelmetComponent
        title={translate('seo.investmentOpportunities.title')}
        description={translate('seo.investmentOpportunities.description')}
        ogTitle={translate('seo.investmentOpportunities.title')}
        ogDescription={translate('seo.investmentOpportunities.description')}
      />
      {isMobile && (
        <>
          <Header onlyMandatesInSearch className={css.header} id="investment-opportunities" />
          <ControlPanel className={css.controlPanel} />
        </>
      )}
      {isMobile ? <Div100vh className={css.contentWrap}>{renderPageContent()}</Div100vh> : renderPageContent()}
    </>
  );
};

export default InvestmentOpportunities;
